import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AlertTriangle,
  Camera,
  Check,
  CheckCircle2,
  Circle,
  CreditCard,
  Eye,
  EyeOff,
  MessageCircle,
  Pencil,
  Save,
  Sparkles,
  X as CancelIcon,
} from 'lucide-react';
import PageScaffold from '../component/pageScaffold/PageScaffold';
import PasswordStrengthMeter from '../component/common/PasswordStrengthMeter';
import { httpClient } from '../common/httpclient';
import { planDisplayName } from '../common/planDisplayName';
import { usePermissions } from '../hooks/usePermissions';
import { useIsMobileShell } from '../hooks/useIsMobileShell';

/**
 * Minha Conta — DESIGN-SYSTEM.md §6.4 / owner-only.
 *
 * Ref: `prototipo-configuracoes-v2.html` (view-conta) — o `DESIGN-SYSTEM.md`
 * (linha 14) marca esse arquivo como a referência atual para
 * Configurações/Minha Conta, superseding `prototipo-configuracoes.html`.
 * Escopo exato deste item do checklist: hero (com anel de completude de
 * perfil, §5.12-style) + 4 abas — Dados da Empresa / Meu Plano / Pagamentos /
 * Segurança — a 5ª aba do protótipo v2 ("Notificações") não está no escopo
 * deste item e não foi implementada.
 *
 * Todas as chamadas de API, payloads e regras de negócio são idênticas à
 * versão anterior deste arquivo — apenas a camada visual/estrutural mudou:
 * - `field-card`/`ro-field` com edição inline por card (§5.5), mesmo padrão
 *   já usado em `customers/View.js` (`FieldCard`/`RoField` locais, cópia do
 *   mesmo idioma — não existe um componente compartilhado hoje, cada tela
 *   define o seu, mesma convenção do restante do app).
 * - Medidor de força de senha na aba Segurança agora reaproveita
 *   `component/common/PasswordStrengthMeter` (o mesmo componente de
 *   `ChangePassword.js`/`Login.js`), como o próprio checklist já indicava —
 *   a versão anterior desta tela não tinha medidor de força nenhum.
 * - Anel de completude de perfil no hero + checklist de progresso: dado
 *   inteiramente derivado do `formData` já carregado (nenhuma chamada de API
 *   nova), mesmo idioma do anel de progresso já usado em
 *   `customers/Form.js`/`products/Form.js`.
 * - Prévia "Como aparece para clientes" no card de Contatos: recalculada ao
 *   vivo a partir do próprio `formData` (mesmo state editado pelos campos),
 *   sem chamada de API nova — mesmo padrão de preview ao vivo de
 *   `customers/Form.js`.
 *
 * Gap de backend documentado (sem inventar dado/endpoint): o protótipo v2
 * também desenha, na aba "Meu plano", uma tabela de "Histórico de faturas"
 * (com pills de status Pago/Pendente/Cancelada) e, na aba "Pagamentos", um
 * fluxo completo de cadastro de cartão. `fv-api` não expõe hoje nenhum
 * endpoint de assinatura/fatura/forma de pagamento para o próprio
 * tenant (só existe `/admin/subscription`, fora de escopo do redesign —
 * ver FRONTEND-TASKS.md "Fora de escopo"). Fabricar essa tabela ou o fluxo
 * de cadastro de cartão exibiria dado financeiro falso a um usuário real de
 * produção, então a aba "Pagamentos" reproduz apenas o estado vazio
 * (§5.11 empty-state) citado pelo `DESIGN-SYSTEM.md` como um dos elementos
 * que o protótipo v2 demonstra ("payment-method cards with an empty
 * state"), e a aba "Meu plano" mantém exatamente os mesmos dados já
 * existentes na versão anterior (nome/preço do plano e contagem de usuários,
 * ambos estáticos desde sempre, sem endpoint próprio; armazenamento
 * continua 100% real via `GET /enterprise/storage-info`). "Autenticação em
 * duas etapas"/"Sessões ativas" (aba Segurança do protótipo) também não têm
 * endpoint hoje e ficaram de fora pela mesma razão — a aba Segurança desta
 * tela preserva exatamente o único fluxo real que já existia (alterar
 * senha via `POST /me/password`).
 */

// `.ro-field` — label uppercase micro + valor, modo leitura do `field-card`
// (cópia do mesmo idioma de `customers/View.js` `RoField`).
function RoField({ label, value, span }) {
  return (
    <Box sx={{ gridColumn: span ? { xs: '1 / -1', sm: 'span 2' } : 'auto', minWidth: 0 }}>
      <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

// `.field-card` — card com cabeçalho (título + botão Editar) e toggle
// "Editar → Salvar/Cancelar" por card (cópia do mesmo idioma de
// `customers/View.js` `FieldCard`).
function FieldCard({ title, editing, onEdit, onCancel, onSave, saving, children, footer }) {
  return (
    <Card elevation={0}>
      <Box sx={{ p: '18px 20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.75 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{title}</Typography>
          {!editing && onEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Pencil size={13} strokeWidth={2} />}
              onClick={onEdit}
            >
              Editar
            </Button>
          )}
        </Box>
        {children}
        {editing && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.25 }}>
            <Button size="small" variant="outlined" startIcon={<CancelIcon size={13} strokeWidth={2} />} onClick={onCancel} disabled={saving}>
              Cancelar
            </Button>
            <Button size="small" variant="contained" startIcon={<Save size={13} strokeWidth={2} />} onClick={onSave} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          </Box>
        )}
        {footer}
      </Box>
    </Card>
  );
}

const formatInt = (n) => Number(n || 0).toLocaleString('pt-BR');

// Texto do período de teste: "Período de teste — 12 dias restantes" / "encerrado".
const trialStatusLabel = (trial) => {
  if (!trial) return '';
  if (trial.expired) return 'Período de teste encerrado';
  const days = trial.days_left;
  return `Período de teste — ${days} ${days === 1 ? 'dia restante' : 'dias restantes'}`;
};

const formatTrialDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

// Card de franquia mensal do plano (mensagens de WhatsApp / tokens de IA):
// usado / total, restante, barra de progresso. total 0 = não incluso no plano.
function QuotaCard({ icon: Icon, title, unit, quota, loading, error, usageAvailable }) {
  const theme = useTheme();
  const included = Boolean(quota && quota.included);
  const percent = included ? Math.min((quota.used / quota.total) * 100, 100) : 0;
  const exhausted = included && quota.exhausted;

  return (
    <Card elevation={0} sx={{ p: '20px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Icon size={16} strokeWidth={2} />
        <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ fontSize: '11px', color: 'text.disabled', ml: 'auto' }}>mensal</Typography>
      </Box>

      {loading ? (
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>Carregando informações...</Typography>
      ) : error ? (
        <Typography sx={{ fontSize: '12px', color: 'error.main' }}>Não foi possível carregar o consumo.</Typography>
      ) : !included ? (
        <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>Não incluso no seu plano.</Typography>
      ) : quota.unlimited ? (
        <>
          <Typography sx={{ fontSize: '22px', fontWeight: 800 }}>
            Ilimitado{' '}
            <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.disabled' }}>
              {formatInt(quota.used)} {unit} usados neste mês
            </Typography>
          </Typography>
          <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 1 }}>
            Sem limite durante o período de teste.
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: '22px', fontWeight: 800, color: exhausted ? 'error.main' : 'text.primary' }}>
            {formatInt(quota.used)}{' '}
            <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.disabled' }}>
              de {formatInt(quota.total)} {unit}
            </Typography>
          </Typography>
          <Box sx={{ height: 10, borderRadius: '8px', backgroundColor: 'background.subtle', overflow: 'hidden', mt: 1 }}>
            <Box sx={{
              height: '100%', borderRadius: '8px',
              background: exhausted ? theme.palette.error.main : theme.palette.gradient.brand,
              width: `${percent}%`, transition: 'width .25s ease',
            }} />
          </Box>
          <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 1 }}>
            {formatInt(quota.remaining)} {unit} restantes neste mês
          </Typography>
          {exhausted && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: 1, fontSize: '12px', fontWeight: 600, color: 'error.main' }}>
              <AlertTriangle size={14} strokeWidth={2.2} />
              Franquia esgotada. Fale com o suporte para ampliar seu plano.
            </Box>
          )}
          {usageAvailable === false && (
            <Typography sx={{ fontSize: '11px', color: 'warning.main', mt: 1 }}>
              Consumo temporariamente indisponível; o valor exibido pode estar desatualizado.
            </Typography>
          )}
        </>
      )}
    </Card>
  );
}

// `.empty-state` (§5.11) — dashed-circle + texto, sem ação (nenhum fluxo
// real de cadastro de cartão existe hoje — ver comentário de cobertura no
// topo do arquivo).
function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6, px: 3, textAlign: 'center' }}>
      <Box sx={{
        width: 72, height: 72, borderRadius: '50%', border: '2px dashed', borderColor: 'divider',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', mb: 0.5,
      }}>
        <Icon size={26} strokeWidth={1.6} />
      </Box>
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 340 }}>{subtitle}</Typography>}
    </Box>
  );
}

// `.tip` — item do checklist de completude do perfil (mesmo idioma de
// `PasswordStrengthMeter`'s CRITERIA list).
function CompletenessTip({ ok, label }) {
  const Icon = ok ? CheckCircle2 : Circle;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11.5px', color: ok ? 'success.main' : 'text.disabled' }}>
      <Icon size={13} strokeWidth={2.5} />
      {label}
    </Box>
  );
}

// `.ffs`/`.ff-input` — casca de campo standalone (52px, radius 12px, fundo
// surface-2, label flutuante interno) — cópia 1:1 de
// `customers/View.js`/`customers/Form.js`/`products/Form.js`
// `standaloneFieldSx`.
function buildStandaloneFieldSx() {
  return {
    '& .MuiInputBase-input': { fontSize: '13.5px' },
    '& .MuiInputLabel-root': {
      fontSize: '13.5px',
      color: 'text.disabled',
      transform: 'translate(14px, 17px) scale(1)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      fontSize: '10px',
      fontWeight: 800,
      color: 'primary.main',
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      transform: 'translate(14px, 8px) scale(1)',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: 'background.subtle',
      height: '52px',
      transition: 'background-color .15s ease, box-shadow .15s ease',
    },
    '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
      padding: '21px 14px 7px 14px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1.5px',
      borderColor: 'divider',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
      borderWidth: '1.5px',
    },
  };
}

const standaloneFieldSx = buildStandaloneFieldSx();
const roGridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '14px 16px' };
const editGridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };

// Anel de progresso (SVG, stroke-dasharray/stroke-dashoffset) — mesmo idioma
// já usado em `customers/Form.js`/`products/Form.js`, com a casca visual do
// `.hero-ring` do protótipo (branco sobre o gradiente, rotacionado -90deg).
const RING_R = 26;
const RING_CIRC = 2 * Math.PI * RING_R;

function HeroRing({ percent, label }) {
  const offset = RING_CIRC - (RING_CIRC * percent) / 100;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', flexShrink: 0, ml: 'auto' }}>
      <Box sx={{ position: 'relative', width: 64, height: 64 }}>
        <svg viewBox="0 0 64 64" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
          <circle cx="32" cy="32" r={RING_R} fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="5" />
          <circle
            cx="32"
            cy="32"
            r={RING_R}
            fill="none"
            stroke="#fff"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={RING_CIRC}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset .4s ease' }}
          />
        </svg>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14.5px', fontWeight: 800, color: '#fff' }}>
          {percent}%
        </Box>
      </Box>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,.85)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {label}
      </Typography>
    </Box>
  );
}

const ACCOUNT_TABS = [
  { value: 'empresa', label: 'Dados da empresa' },
  { value: 'plano', label: 'Meu plano' },
  { value: 'pagamentos', label: 'Pagamentos' },
  { value: 'seguranca', label: 'Segurança' },
];

export default function MyAccount() {
  const theme = useTheme();
  const isMobileShell = useIsMobileShell();
  const navigate = useNavigate();
  const { userData, loading } = usePermissions();
  const [activeTab, setActiveTab] = useState('empresa');
  const [editMode, setEditMode] = useState({
    dados: false,
    contatos: false
  });

  // Dados do formulário
  const [formData, setFormData] = useState({
    // Dados Cadastrais
    trade_name: '',
    legal_name: '',
    cnpj: '',
    address: '',
    number_address: '',
    neighborhood: '',
    city: '',
    state: '',
    zip_code: '',
    phone_number: '',
    cell_phone: '',
    email: '',
    website: '',
    facebook: '',
    instagram: '',
    complement: '',
    instagram_link_qr_code: '',
  });

  // Estados para upload do logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState('success');
  const [message, setMessage] = useState('');
  const [storageInfo, setStorageInfo] = useState({
    total_size_bytes: 0,
    total_size_mb: '0',
    file_count: 0,
    maximum_size_upload: 0
  });
  const [storageLoading, setStorageLoading] = useState(false);
  // Plano contratado + consumo mensal (WhatsApp / IA) + módulos: GET /enterprise/plan-usage
  const [planUsage, setPlanUsage] = useState(null);
  const [planUsageLoading, setPlanUsageLoading] = useState(false);
  const [planUsageError, setPlanUsageError] = useState(false);

  // Estados para alteração de senha
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Verificar se o usuário é owner
  useEffect(() => {
    if (!loading && (!userData || userData.is_owner !== true)) {
      navigate('/home');
    }
  }, [userData, loading, navigate]);

  // Função para buscar o logo da empresa
  const fetchCompanyLogo = async () => {
    setLogoLoading(true);
    try {
      const response = await httpClient.get('/enterprise/cfg?name=sidebar_icon&category=general');

      if (response.status === 200 && response.data) {
        // Se o logo existe, definir como preview
        setLogoPreview(response.data.value);

      } else {
        // Se não há logo, manter o estado vazio
        console.log('Nenhum logo encontrado para a empresa');
      }
    } catch (error) {
      console.error('Erro ao buscar logo da empresa:', error);
      // Não mostrar erro para o usuário, apenas logar
    } finally {
      setLogoLoading(false);
    }
  };

  const fetchCompanyData = async () => {

    const response = await httpClient.get('/enterprise/account-info');

    if (response.status === 200 && response.data) {
      setFormData(response.data);
    }
  };

  const fetchStorageInfo = async () => {
    setStorageLoading(true);
    try {
      const response = await httpClient.get('/enterprise/storage-info');

      if (response.status === 200 && response.data) {
        setStorageInfo(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar informações de armazenamento:', error);
    } finally {
      setStorageLoading(false);
    }
  };

  const fetchPlanUsage = async () => {
    setPlanUsageLoading(true);
    setPlanUsageError(false);
    try {
      const response = await httpClient.get('/enterprise/plan-usage');

      if (response.status === 200 && response.data) {
        setPlanUsage(response.data);
      } else {
        setPlanUsageError(true);
      }
    } catch (error) {
      console.error('Erro ao buscar plano e consumo:', error);
      setPlanUsageError(true);
    } finally {
      setPlanUsageLoading(false);
    }
  };

  // Carregar logo da empresa ao montar o componente
  useEffect(() => {
    if (userData && userData.is_owner === true && !loading) {
      fetchCompanyLogo();
      fetchCompanyData();
      fetchStorageInfo();
      fetchPlanUsage();
    }
  }, [userData, loading]);

  // Mostrar loading enquanto verifica permissões
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Se não for owner, não renderizar nada (será redirecionado)
  if (!userData || userData.is_owner !== true) {
    return null;
  }

  const handleEditToggle = (section) => {
    setEditMode(prev => ({
      ...prev,
      [section]: true
    }));
  };

  const handleSave = (section) => {
    if (section === 'dados') {
      handleUpdateCompanyData(formData);
    } else {
      handleUpdateCompanyContact(formData);
    }
    setEditMode(prev => ({
      ...prev,
      [section]: false
    }));
    // Aqui você pode adicionar a lógica para salvar os dados
  };

  const handleCancel = (section) => {
    setEditMode(prev => ({
      ...prev,
      [section]: false
    }));
    // Aqui você pode adicionar a lógica para cancelar as alterações
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Funções para upload do logo
  const handleLogoUpload = async (file) => {
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setOpenSnackbar(true);
      setSeverity('error');
      setMessage('Tipo de arquivo não suportado. Use JPG, PNG ou GIF.');

      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setOpenSnackbar(true);
      setSeverity('error');
      setMessage('Arquivo muito grande. Tamanho máximo: 5MB.');

      return;
    }

    setLogoUploading(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await httpClient.postFile('/enterprise/upload-logo?configName=sidebar_icon', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200 || response.status === 201) {
        // Criar preview do logo
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoPreview(e.target.result);
        };
        reader.readAsDataURL(file);

        setLogoFile(file);
        setOpenSnackbar(true);
        setSeverity('success');
        setMessage('Logo enviado com sucesso!');
      } else {
        setOpenSnackbar(true);
        setSeverity('error');
        setMessage('Erro no upload');
      }
    } catch (error) {
      console.error('Erro no upload do logo:', error);
      setOpenSnackbar(true);
      setSeverity('error');
      setMessage('Erro ao enviar logo. Tente novamente.');
    } finally {
      setLogoUploading(false);
    }

  };

  const handleLogoFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleLogoUpload(file);
    }
  };

  const handleAvatarClick = () => {
    document.getElementById('avatar-logo-upload').click();
  };

  const handleUpdateCompanyContact = async (data) => {
    const response = await httpClient.put('/enterprise/account-info/contact', data);
    if (response.status === 204) {

      setOpenSnackbar(true);
      setSeverity('success');
      setMessage('Contatos atualizados com sucesso!');
    } else {
      setOpenSnackbar(true);
      setSeverity('error');
      setMessage('Erro ao atualizar contatos');
    }
  };

  const handleUpdateCompanyData = async (data) => {
    data.number_address = parseInt(data.number_address);
    const response = await httpClient.put('/enterprise/account-info', data);
    if (response.status === 204) {
      setOpenSnackbar(true);
      setSeverity('success');
      setMessage('Dados atualizados com sucesso!');
    } else {
      setOpenSnackbar(true);
      setSeverity('error');
      setMessage('Erro ao atualizar dados');
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para decodificar o token JWT
  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Função para fazer login e obter novos tokens
  const refreshTokensAfterPasswordChange = async (username, codeClient, newPassword) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code_client: codeClient,
          password: newPassword,
          username: username
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Ler token antigo antes de atualizar
        const oldToken = localStorage.getItem('access_token');
        let oldTokenData = null;
        if (oldToken) {
          try {
            oldTokenData = decodeToken(oldToken);
          } catch (e) {
            // Ignorar erros ao decodificar token antigo
            console.warn('Não foi possível decodificar token antigo');
          }
        }

        // Limpar cache de permissões antes de atualizar tokens
        localStorage.removeItem('user_permissions_cache');

        // Limpar módulos antigos do localStorage antes de atualizar tokens
        if (oldTokenData && oldTokenData.modules) {
          oldTokenData.modules.forEach(module => {
            if (localStorage.getItem(module) === 'true') {
              localStorage.removeItem(module);
            }
          });
        }

        // Atualizar tokens no localStorage
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);

        // Atualizar módulos no localStorage com os novos módulos
        const tokenData = decodeToken(data.access_token);
        if (tokenData && tokenData.modules) {
          tokenData.modules.forEach(module => {
            localStorage.setItem(module, true);
          });
        }

        return true;
      } else {
        console.error('Erro ao obter novos tokens após alteração de senha');
        return false;
      }
    } catch (error) {
      console.error('Erro ao fazer login após alteração de senha:', error);
      return false;
    }
  };

  const handleChangePassword = async () => {
    // Validação básica
    if (!passwordData.current_password || !passwordData.new_password) {
      setOpenSnackbar(true);
      setSeverity('error');
      setMessage('Por favor, preencha todos os campos');
      return;
    }

    if (passwordData.current_password === passwordData.new_password) {
      setOpenSnackbar(true);
      setSeverity('error');
      setMessage('A nova senha deve ser diferente da senha atual');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await httpClient.post('/me/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      if (response.status === 200 || response.status === 204) {
        // Verificar se a resposta contém novos tokens
        let tokensUpdated = false;

        if (response.data && response.data.access_token && response.data.refresh_token) {
          // Se a API retornou tokens na resposta, usar eles
          // Ler token antigo antes de atualizar
          const oldToken = localStorage.getItem('access_token');
          let oldTokenData = null;
          if (oldToken) {
            try {
              oldTokenData = decodeToken(oldToken);
            } catch (e) {
              console.warn('Não foi possível decodificar token antigo');
            }
          }

          // Limpar cache de permissões antes de atualizar tokens
          localStorage.removeItem('user_permissions_cache');

          // Limpar módulos antigos do localStorage antes de atualizar tokens
          if (oldTokenData && oldTokenData.modules) {
            oldTokenData.modules.forEach(module => {
              if (localStorage.getItem(module) === 'true') {
                localStorage.removeItem(module);
              }
            });
          }

          // Atualizar tokens no localStorage
          localStorage.setItem('access_token', response.data.access_token);
          localStorage.setItem('refresh_token', response.data.refresh_token);

          // Atualizar módulos se necessário
          const tokenData = decodeToken(response.data.access_token);
          if (tokenData && tokenData.modules) {
            tokenData.modules.forEach(module => {
              localStorage.setItem(module, true);
            });
          }

          tokensUpdated = true;
        } else {
          // Se não retornou tokens, fazer login novamente com a nova senha
          const currentToken = localStorage.getItem('access_token');
          if (currentToken) {
            const tokenData = decodeToken(currentToken);
            if (tokenData && tokenData.username && tokenData.code_client) {
              tokensUpdated = await refreshTokensAfterPasswordChange(
                tokenData.username,
                tokenData.code_client,
                passwordData.new_password
              );
            } else if (tokenData && tokenData.username && userData && userData.username) {
              // Fallback: usar username do token e tentar encontrar code_client
              // Se não encontrar no token, pode ser que precise ser obtido de outra forma
              console.warn('code_client não encontrado no token, tentando com username apenas');
            }
          }
        }

        if (tokensUpdated) {
          setOpenSnackbar(true);
          setSeverity('success');
          setMessage('Senha alterada com sucesso! Você permanece logado.');
        } else {
          setOpenSnackbar(true);
          setSeverity('warning');
          setMessage('Senha alterada com sucesso! Por favor, faça login novamente.');
        }

        // Limpar campos após sucesso
        setPasswordData({
          current_password: '',
          new_password: ''
        });
      } else {
        const errorMessage = response.data?.message || 'Erro ao alterar senha';
        setOpenSnackbar(true);
        setSeverity('error');
        setMessage(errorMessage);
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao alterar senha. Tente novamente.';
      setOpenSnackbar(true);
      setSeverity('error');
      setMessage(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  // Anel/checklist de completude do perfil (hero) — derivado só do `formData`
  // e do `logoPreview` já carregados, sem chamada de API nova. Mesmo critério
  // de `checkStrength()`/`recalcCompleteness()` do protótipo v2 (adaptado aos
  // nomes reais de campo desta tela).
  const completenessChecks = {
    cadastrais: Boolean(formData.legal_name && formData.cnpj && formData.address),
    contato: Boolean((formData.cell_phone || formData.phone_number) && formData.email),
    redes: Boolean(formData.facebook || formData.instagram),
    logo: Boolean(logoPreview),
  };
  const completenessValues = Object.values(completenessChecks);
  const completenessPercent = Math.round(
    (completenessValues.filter(Boolean).length / completenessValues.length) * 100
  );

  const heroEditProfile = () => {
    setActiveTab('empresa');
    handleEditToggle('dados');
  };

  const links = [];

  return (
    <PageScaffold
      links={links}
      titlePageTarget="Minha Conta"
      loading={false}
      setOpenSnackbar={setOpenSnackbar}
      openSnackbar={openSnackbar}
      severity={severity}
      message={message}
    >
      <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', pb: '40px' }}>
        {/* account-hero — gradiente de marca + avatar + anel de completude (§5.2/§5.12) */}
        <Card elevation={0} sx={{
          borderRadius: '20px',
          mb: 2.25,
          padding: { xs: '22px 20px', sm: '26px' },
          background: theme.palette.gradient.brand,
          boxShadow: theme.customShadows.md,
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          // Mobile (minha-conta.html): cabeçalho centralizado em coluna.
          ...(isMobileShell ? { flexDirection: 'column', textAlign: 'center', gap: '10px', borderRadius: '16px', padding: '16px', mb: '4px' } : {}),
        }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Box sx={{
              width: isMobileShell ? 76 : 78, height: isMobileShell ? 76 : 78, borderRadius: isMobileShell ? '50%' : '22px',
              bgcolor: 'rgba(255,255,255,.18)', border: '2px solid rgba(255,255,255,.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '26px', fontWeight: 800, color: '#fff', overflow: 'hidden',
            }}>
              {logoLoading ? (
                <CircularProgress size={26} sx={{ color: '#fff' }} />
              ) : logoPreview ? (
                <img src={logoPreview} alt="Logo da empresa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                (formData.legal_name || formData.trade_name || '?').trim()[0]?.toUpperCase() || '?'
              )}
            </Box>
            <IconButton
              onClick={handleAvatarClick}
              disabled={logoUploading || logoLoading}
              sx={{
                position: 'absolute', right: -4, bottom: -4, width: 28, height: 28,
                bgcolor: '#fff', color: 'primary.main', boxShadow: theme.customShadows.sm,
                '&:hover': { bgcolor: '#fff' },
              }}
            >
              {logoUploading ? <CircularProgress size={14} /> : <Camera size={13} strokeWidth={2.2} />}
            </IconButton>
            <input
              id="avatar-logo-upload"
              type="file"
              accept="image/*"
              onChange={handleLogoFileSelect}
              style={{ display: 'none' }}
              disabled={logoUploading || logoLoading}
            />
          </Box>

          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <Typography component="h1" sx={{ fontSize: isMobileShell ? '18px' : '19px', fontWeight: 800, color: '#fff', mb: '4px' }} noWrap>
              {formData.legal_name || 'Minha empresa'}
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'rgba(255,255,255,.9)' }} noWrap>
              {[formData.email, formData.city].filter(Boolean).join(' · ') || '—'}
            </Typography>
            {planUsage?.has_plan && (
              <Box sx={{ display: 'flex', gap: 1, mt: '10px', flexWrap: 'wrap', justifyContent: isMobileShell ? 'center' : 'flex-start' }}>
                <Box sx={{ fontSize: '11px', fontWeight: 700, padding: '5px 11px', borderRadius: '20px', bgcolor: 'rgba(255,255,255,.18)', color: '#fff' }}>
                  {planUsage.trial ? trialStatusLabel(planUsage.trial) : `Plano ${planDisplayName(planUsage.plan_name)}`}
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', ...(isMobileShell ? {} : { display: 'contents' }) }}>
            <HeroRing percent={completenessPercent} label="Perfil" />

          <Tooltip title="Editar dados cadastrais">
            <IconButton
              onClick={heroEditProfile}
              sx={{
                width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
                bgcolor: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.28)', color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,.24)' },
              }}
            >
              <Pencil size={15} strokeWidth={2} />
            </IconButton>
          </Tooltip>
          </Box>
        </Card>

        {/* seg-tabs (§9 ToggleButtonGroup) — Dados da empresa / Meu plano / Pagamentos / Segurança */}
        {isMobileShell ? (
          // Mobile: abas sublinhadas roláveis (`.tabs-u` do protótipo).
          <Box role="tablist" sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider', m: '0 -16px 16px', p: '0 16px', overflowX: 'auto', gap: '22px' }}>
            {ACCOUNT_TABS.map((tab) => (
              <Box
                key={tab.value}
                component="button"
                type="button"
                role="tab"
                aria-selected={activeTab === tab.value}
                onClick={() => setActiveTab(tab.value)}
                sx={{ height: 44, fontWeight: 700, fontSize: 13.5, fontFamily: 'inherit', color: activeTab === tab.value ? 'primary.main' : 'text.disabled', whiteSpace: 'nowrap', border: 0, borderBottom: '3px solid', borderBottomColor: activeTab === tab.value ? 'primary.main' : 'transparent', background: 'none', mb: '-1px', p: 0, cursor: 'pointer', flexShrink: 0 }}
              >
                {tab.label}
              </Box>
            ))}
          </Box>
        ) : (
        <ToggleButtonGroup
          exclusive
          value={activeTab}
          onChange={(e, v) => v && setActiveTab(v)}
          sx={{ mb: 2.25, width: 'fit-content', flexWrap: 'wrap' }}
        >
          {ACCOUNT_TABS.map((tab) => (
            <ToggleButton key={tab.value} value={tab.value} sx={{ px: 2.25 }}>
              {tab.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        )}

        {/* ABA: DADOS DA EMPRESA */}
        {activeTab === 'empresa' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <FieldCard
              title="Dados cadastrais"
              editing={editMode.dados}
              onEdit={() => handleEditToggle('dados')}
              onCancel={() => handleCancel('dados')}
              onSave={() => handleSave('dados')}
              footer={
                !editMode.dados && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mt: 2, pt: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
                    <CompletenessTip ok={completenessChecks.cadastrais} label="Dados cadastrais completos" />
                    <CompletenessTip ok={completenessChecks.contato} label="Contato principal completo" />
                    <CompletenessTip ok={completenessChecks.redes} label="Redes sociais preenchidas" />
                    <CompletenessTip ok={completenessChecks.logo} label="Logo da empresa enviada" />
                  </Box>
                )
              }
            >
              {!editMode.dados ? (
                <Box sx={roGridSx}>
                  <RoField span label="Razão social" value={formData.legal_name} />
                  <RoField label="Nome fantasia" value={formData.trade_name} />
                  <RoField label="CNPJ" value={formData.cnpj} />
                  <RoField span label="Endereço" value={formData.address} />
                  <RoField label="Número" value={formData.number_address} />
                  <RoField label="CEP" value={formData.zip_code} />
                  <RoField label="Bairro" value={formData.neighborhood} />
                  <RoField label="Cidade" value={formData.city} />
                  <RoField label="Estado" value={formData.state} />
                </Box>
              ) : (
                <Box sx={editGridSx}>
                  <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                    <TextField fullWidth label="Razão social" variant="outlined" sx={standaloneFieldSx}
                      value={formData.legal_name || ''} onChange={(e) => handleInputChange('legal_name', e.target.value)} />
                  </Box>
                  <TextField fullWidth label="Nome fantasia" variant="outlined" sx={standaloneFieldSx}
                    value={formData.trade_name || ''} onChange={(e) => handleInputChange('trade_name', e.target.value)} />
                  <TextField fullWidth label="CNPJ" variant="outlined" sx={standaloneFieldSx}
                    value={formData.cnpj || ''} onChange={(e) => handleInputChange('cnpj', e.target.value)} />
                  <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                    <TextField fullWidth label="Endereço" variant="outlined" sx={standaloneFieldSx}
                      value={formData.address || ''} onChange={(e) => handleInputChange('address', e.target.value)} />
                  </Box>
                  <TextField fullWidth label="Número" type="number" variant="outlined" sx={standaloneFieldSx}
                    value={formData.number_address ?? ''} onChange={(e) => handleInputChange('number_address', e.target.value)} />
                  <TextField fullWidth label="CEP" variant="outlined" sx={standaloneFieldSx}
                    value={formData.zip_code || ''} onChange={(e) => handleInputChange('zip_code', e.target.value)} />
                  <TextField fullWidth label="Bairro" variant="outlined" sx={standaloneFieldSx}
                    value={formData.neighborhood || ''} onChange={(e) => handleInputChange('neighborhood', e.target.value)} />
                  <TextField fullWidth label="Cidade" variant="outlined" sx={standaloneFieldSx}
                    value={formData.city || ''} onChange={(e) => handleInputChange('city', e.target.value)} />
                  <TextField fullWidth label="Estado" variant="outlined" sx={standaloneFieldSx}
                    value={formData.state || ''} onChange={(e) => handleInputChange('state', e.target.value)} />
                </Box>
              )}
            </FieldCard>

            <FieldCard
              title="Contatos e redes sociais"
              editing={editMode.contatos}
              onEdit={() => handleEditToggle('contatos')}
              onCancel={() => handleCancel('contatos')}
              onSave={() => handleSave('contatos')}
            >
              {!editMode.contatos ? (
                <Box sx={roGridSx}>
                  <RoField label="Telefone principal" value={formData.cell_phone} />
                  <RoField label="Telefone secundário" value={formData.phone_number} />
                  <RoField span label="E-mail" value={formData.email} />
                  <RoField span label="Website" value={formData.website} />
                  <RoField label="Instagram" value={formData.instagram} />
                  <RoField label="Facebook" value={formData.facebook} />
                </Box>
              ) : (
                <Box sx={editGridSx}>
                  <TextField fullWidth label="Telefone principal" variant="outlined" sx={standaloneFieldSx}
                    value={formData.cell_phone || ''} onChange={(e) => handleInputChange('cell_phone', e.target.value)} />
                  <TextField fullWidth label="Telefone secundário" variant="outlined" sx={standaloneFieldSx}
                    value={formData.phone_number || ''} onChange={(e) => handleInputChange('phone_number', e.target.value)} />
                  <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                    <TextField fullWidth label="E-mail" variant="outlined" sx={standaloneFieldSx}
                      value={formData.email || ''} onChange={(e) => handleInputChange('email', e.target.value)} />
                  </Box>
                  <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                    <TextField fullWidth label="Website" variant="outlined" sx={standaloneFieldSx}
                      value={formData.website || ''} onChange={(e) => handleInputChange('website', e.target.value)} />
                  </Box>
                  <TextField fullWidth label="Instagram" variant="outlined" sx={standaloneFieldSx}
                    value={formData.instagram || ''} onChange={(e) => handleInputChange('instagram', e.target.value)} />
                  <TextField fullWidth label="Facebook" variant="outlined" sx={standaloneFieldSx}
                    value={formData.facebook || ''} onChange={(e) => handleInputChange('facebook', e.target.value)} />
                </Box>
              )}

              {/* preview-box — "Como aparece para clientes" (biz-card), recalculada ao
                  vivo a partir do próprio `formData` (mesmo state editado acima). */}
              <Box sx={{ mt: 2.5, p: '14px', borderRadius: '12px', backgroundColor: 'background.subtle', border: '1px dashed', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: 1 }}>
                  Como aparece para clientes
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '10px', flexShrink: 0, overflow: 'hidden',
                    bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', fontWeight: 800,
                  }}>
                    {logoPreview ? (
                      <img src={logoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      (formData.trade_name || formData.legal_name || '?').trim()[0]?.toUpperCase() || '?'
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700 }} noWrap>
                      {formData.trade_name || formData.legal_name || 'Sua empresa'}
                    </Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }} noWrap>
                      {formData.cell_phone || formData.phone_number || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }} noWrap>
                      {formData.email || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }} noWrap>
                      {formData.website || '—'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </FieldCard>
          </Box>
        )}

        {/* ABA: MEU PLANO */}
        {activeTab === 'plano' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(260px, 1fr))' }, gap: 2 }}>
            <Card elevation={0} sx={{
              p: '20px',
              background: `linear-gradient(135deg, ${theme.palette.secondary.bg}, ${theme.palette.info.bg})`,
            }}>
              {planUsageLoading && !planUsage ? (
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>Carregando plano...</Typography>
              ) : planUsageError && !planUsage ? (
                <Typography sx={{ fontSize: '12px', color: 'error.main' }}>Não foi possível carregar o plano.</Typography>
              ) : planUsage?.has_plan && planUsage.trial ? (
                <>
                  <Typography sx={{ fontSize: '16px', fontWeight: 800 }}>{trialStatusLabel(planUsage.trial)}</Typography>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: '4px' }}>
                    {planUsage.trial.expired ? 'Terminou em' : 'Termina em'} {formatTrialDate(planUsage.trial.ends_at)}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: planUsage.trial.expired ? 'error.main' : 'text.disabled', mt: '4px' }}>
                    {planUsage.trial.expired
                      ? 'Você agora está no plano grátis. Assine um plano para liberar mais recursos. Seus dados foram mantidos.'
                      : 'Todas as funcionalidades estão liberadas, sem limites, durante o teste.'}
                  </Typography>
                </>
              ) : planUsage?.has_plan ? (
                <>
                  <Typography sx={{ fontSize: '16px', fontWeight: 800 }}>Plano {planDisplayName(planUsage.plan_name)}</Typography>
                  <Typography sx={{ fontSize: '22px', fontWeight: 800, color: 'primary.main', mt: '4px' }}>
                    R$ {formatInt(planUsage.value_reais)},{String(planUsage.value_cents || 0).padStart(2, '0')}{' '}
                    <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.disabled' }}>/mês</Typography>
                  </Typography>
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: '16px', fontWeight: 800 }}>Sem plano ativo</Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled', mt: '4px' }}>
                    Nenhuma assinatura encontrada para esta empresa.
                  </Typography>
                </>
              )}
              <Button variant="outlined" size="small" sx={{ mt: 2 }} onClick={() => navigate('/plans')}>
                Ver outros planos
              </Button>
            </Card>

            <Card elevation={0} sx={{ p: '20px' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 1.25 }}>Armazenamento</Typography>
              <Box sx={{ height: 10, borderRadius: '8px', backgroundColor: 'background.subtle', overflow: 'hidden' }}>
                <Box sx={{
                  height: '100%', borderRadius: '8px',
                  background: theme.palette.gradient.brand,
                  width: `${(() => {
                    if (storageLoading || !storageInfo.maximum_size_upload) return 0;
                    const used = parseFloat(storageInfo.total_size_mb) || 0;
                    const max = parseFloat(storageInfo.maximum_size_upload) || 1;
                    return Math.min((used / max) * 100, 100);
                  })()}%`,
                  transition: 'width .25s ease',
                }} />
              </Box>
              <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 1 }}>
                {storageLoading
                  ? 'Carregando informações...'
                  : planUsage?.trial
                    ? `${storageInfo.total_size_mb} MB usados · Ilimitado no período de teste · ${storageInfo.file_count} arquivo(s)`
                    : `${storageInfo.total_size_mb} MB usados de ${storageInfo.maximum_size_upload || 0} MB · ${storageInfo.file_count} arquivo(s)`}
              </Typography>
            </Card>

            <QuotaCard
              icon={MessageCircle}
              title="Mensagens de WhatsApp"
              unit="mensagens"
              quota={planUsage?.whatsapp}
              loading={planUsageLoading && !planUsage}
              error={planUsageError && !planUsage}
              usageAvailable={planUsage?.usage_available}
            />

            <QuotaCard
              icon={Sparkles}
              title="Assistente de IA"
              unit="tokens"
              quota={planUsage?.ai}
              loading={planUsageLoading && !planUsage}
              error={planUsageError && !planUsage}
              usageAvailable={planUsage?.usage_available}
            />

            <Card elevation={0} sx={{ p: '20px', gridColumn: { sm: '1 / -1' } }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 1.25 }}>Funcionalidades do plano</Typography>
              {planUsageLoading && !planUsage ? (
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>Carregando informações...</Typography>
              ) : planUsageError && !planUsage ? (
                <Typography sx={{ fontSize: '12px', color: 'error.main' }}>Não foi possível carregar as funcionalidades.</Typography>
              ) : !planUsage?.has_plan || !planUsage.modules?.length ? (
                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>Nenhuma funcionalidade ativa no plano.</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px 20px' }}>
                  {planUsage.modules.map((mod) => (
                    <Box key={mod.key} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', minWidth: 0 }}>
                      <Box sx={{ color: 'success.main', mt: '2px', flexShrink: 0 }}>
                        <Check size={15} strokeWidth={2.6} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>{mod.label}</Typography>
                        {mod.description && (
                          <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{mod.description}</Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>

            <Card elevation={0} sx={{ p: '20px', gridColumn: { sm: '1 / -1' } }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 1.25 }}>Usuários</Typography>
              <Box sx={{ height: 10, borderRadius: '8px', backgroundColor: 'background.subtle', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', borderRadius: '8px', background: theme.palette.gradient.brand, width: '50%' }} />
              </Box>
              <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 1 }}>
                1 de 2 usuários ativos nas últimas 24h
              </Typography>
            </Card>
          </Box>
        )}

        {/* ABA: PAGAMENTOS — ver comentário de cobertura no topo do arquivo
            (sem endpoint de forma de pagamento hoje, apenas o estado vazio). */}
        {activeTab === 'pagamentos' && (
          <Card elevation={0} sx={{ maxWidth: 640 }}>
            <Box sx={{ p: '18px 20px' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 1 }}>Formas de pagamento</Typography>
              <EmptyState
                icon={CreditCard}
                title="Nenhuma forma de pagamento cadastrada"
                subtitle="Cartões usados para cobrança da assinatura aparecerão aqui."
              />
            </Box>
          </Card>
        )}

        {/* ABA: SEGURANÇA */}
        {activeTab === 'seguranca' && (
          <Card elevation={0} sx={{ maxWidth: 480 }}>
            <Box sx={{ p: '18px 20px' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 1.75 }}>Alterar senha</Typography>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Senha atual"
                  type={showCurrentPassword ? 'text' : 'password'}
                  variant="outlined"
                  sx={standaloneFieldSx}
                  value={passwordData.current_password}
                  onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" size="small">
                          {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Nova senha"
                  type={showNewPassword ? 'text' : 'password'}
                  variant="outlined"
                  sx={standaloneFieldSx}
                  value={passwordData.new_password}
                  onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                          {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <PasswordStrengthMeter password={passwordData.new_password} sx={{ mt: 1 }} />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={passwordLoading}
                  onClick={() => setPasswordData({ current_password: '', new_password: '' })}
                >
                  Limpar
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={passwordLoading || !passwordData.current_password || !passwordData.new_password}
                  onClick={handleChangePassword}
                  startIcon={passwordLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                >
                  {passwordLoading ? 'Alterando…' : 'Alterar senha'}
                </Button>
              </Box>
            </Box>
          </Card>
        )}
      </Box>
    </PageScaffold>
  );
}
